<template>
    <cr-navigation-bar>
        <router-link to="/" :aria-label="t('HEADER_HOME_LOGO_LINK_LABEL')">
            <img :src="defaultLogo" class="logo" id="defaultLogo" alt="">
        </router-link>

        <cr-spacer />

        <template v-if="sessionExpirationTimestamp">
            <template v-if="timeRemainingInSeconds === 0">
                <span class="title" data-testing="range-session-ended">{{ t('CYBER_RANGE_ENVIRONMENT_ENDED_MESSAGE') }}</span>
                <cr-spacer/>
            </template>
            <template v-else>
                <span>
                    <cr-external-link :href="environmentTimeRemainingLink" icon-left data-testing="range-time-remaining-help-link">
                        <span class="title">{{ t('CYBER_RANGE_ENVIRONMENT_TIME_REMAINING') }}</span>
                    </cr-external-link>
                    <span class="title cr-ml2" data-testing="range-time-remaining">{{ timeRemainingFormatted }}</span>
                </span>

                <span v-if="showClockOutOfSyncMessage" class="cr-ml2" data-testing="range-clock-out-of-sync">
                    ( <a :href="clockOutOfSync" target="_blank" class="cr-link">
                        {{ t('CYBER_RANGE_CLOCK_OUT_OF_SYNC_MESSAGE') }}
                    </a> )
                </span>

                <cr-spacer />

                <cr-button background-color="cr-confirm" text-color="cr-confirm-text" data-testing="range-terminate-session-button" @click="showTerminateSessionDialog">
                    <cr-icon class="cr-pr2">bi-trash3-fill</cr-icon>{{ t('CYBER_RANGE_ENVIRONMENT_EARLY_TERMINATION_BUTTON') }}
                </cr-button>
                <confirmation-dialog v-model="isTerminateSessionDialogShowing" formInstruction checkbox :confirmation-message="t('CYBER_RANGE_ENVIRONMENT_EARLY_TERMINATION_DIALOG_UNDERSTANDING_LABEL')" :title="t('CYBER_RANGE_ENVIRONMENT_EARLY_TERMINATION_DIALOG_TITLE')" :confirm-text="t('CYBER_RANGE_ENVIRONMENT_EARLY_TERMINATION_DIALOG_CONFIRM')" @confirm="onTerminateConfirmed" data-testing="range-terminate-session-dialog">
                    <span data-testing="range-terminate-session-dialog-confirm-text">{{ t('CYBER_RANGE_ENVIRONMENT_EARLY_TERMINATION_DIALOG_CONFIRMATION_TEXT') }}</span>
                </confirmation-dialog>
            </template>
        </template>
    </cr-navigation-bar>
</template>

<style scoped>
.cr-navbar {
    padding: 0.5rem 0;
    min-height: var(--cr-nav-bar-height);
    background-color: light-dark(white, #272727);
}
.cr-external-link,
.cr-link {
    color: light-dark(var(--cr-primary-link), #4190e9);
}
.logo {
    max-width: 220px;
    max-height: 48px;
    height: 48px;
}
.title {
    font-size: 1.25rem;
    font-weight: bold;
}
.comicbook .confirm
{
  color: black;
}
</style>

<script setup lang="ts">
import defaultLightLogo from '@/assets/logo/cloud_ctf_light.svg';
import defaultDarkLogo from '@/assets/logo/cloud_ctf_dark.svg';
import { useThemeStore } from '@/stores/themeStore';
import { computed, getCurrentInstance, onMounted, onUnmounted, ref } from "vue";
import { useChallengeEnvironmentStore } from '../../stores/challengeEnvironmentStore';
import { useKbStore } from '../../stores/kbStore';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const isTerminateSessionDialogShowing = ref(false);
const showTerminateSessionDialog = () => isTerminateSessionDialogShowing.value = true;

const props = defineProps<{
    challengeEnvironmentId: string,
    sessionExpirationTimestamp?: string,
    showClockOutOfSyncMessage: boolean
}>();
const emit = defineEmits<{
    (name: 'session-ended'): void
}>();

function toHumanDurationDigital(duration: number): string
{
    const hours = Math.floor(duration / 3600);
    const minutes = Math.floor((duration % 3600) / 60);
    const seconds = Math.round(duration % 60);
    return [
        hours,
        minutes.toString().padStart(2, '0'),
        seconds.toString().padStart(2, '0'),
    ].join(':')
}

const kbStore = useKbStore();
const environmentTimeRemainingLink = kbStore.environmentTimeRemaining
const clockOutOfSync = kbStore.clockOutOfSync

const timeRemainingInSeconds = ref(0);
const timeRemainingFormatted = computed(() => toHumanDurationDigital(timeRemainingInSeconds.value))

function getRemainingTimeInSeconds()
{
    const remainingTime = Math.floor((endDate.value.getTime() - new Date().getTime()) / 1000);
    return Math.max(0, remainingTime);
}

const endDate = computed(() => new Date(props.sessionExpirationTimestamp))

let timerId:NodeJS.Timeout;

async function endSession()
{
    clearInterval(timerId);
    timeRemainingInSeconds.value = 0;

    try
    {
        await useChallengeEnvironmentStore().terminate(props.challengeEnvironmentId);
    }
    catch (error) {}
    emit('session-ended');
}
async function onTerminateConfirmed()
{
    await endSession()
    window.close();
}

onMounted(() =>
{
    timeRemainingInSeconds.value = getRemainingTimeInSeconds();
    timerId = setInterval(() =>
    {
        timeRemainingInSeconds.value = getRemainingTimeInSeconds();
        if (timeRemainingInSeconds.value <= 0)
        {
            endSession();
        }
    }, 1000);
});
onUnmounted(() => clearInterval(timerId));

// Theme
const themeStore = useThemeStore();
const defaultLogo = computed(() => themeStore.isDark ? defaultDarkLogo : defaultLightLogo);
</script>
